import { useEffect, useState } from "react";
import { Dialog, ButtonVariant } from "@merge-api/merge-javascript-shared";
import { LinkedAccount } from "../../../../../models/Entities";
import useFetchTestLinkedAccounts from "../../../../blueprint-editor/left-panel/hooks/useFetchTestLinkedAccounts";
import LinkedAccountSelector from "./components/LinkedAccountSelector";
import OverrideLastRunAtField from "./components/OverrideLastRunAtField";
import CMDCheckboxLabel from "./components/CMDCheckboxLabel";
import { DiffRunFormData } from "./entities";
import CappedNumberField from "./components/CappedNumberField";

interface CommonModelDiffRunModalProps {
  blueprintCategory: string | null;
  integrationID: string;
  open: boolean;
  onClose: () => void;
  onConfirm: (config: DiffRunFormData) => void;
}

const CommonModelDiffRunModal = ({
  blueprintCategory,
  integrationID,
  open,
  onClose,
  onConfirm,
}: CommonModelDiffRunModalProps) => {
  // State for form values
  const [overrideLastRunAt, setOverrideLastRunAt] = useState<string | undefined>(undefined);
  const [maxPageIterations, setMaxPageIterations] = useState<number | undefined>(5);
  const [maxLoopIterations, setMaxLoopIterations] = useState<number | undefined>(10);
  const [disableFilterByDate, setDisableFilterByDate] = useState<boolean>(true);
  const [respectLinkedAccountScopes, setRespectLinkedAccountScopes] = useState<boolean>(false);

  // Linked account state
  const [isLoadingTestLinkedAccounts, setIsLoadingTestLinkedAccounts] = useState<boolean>(false);
  const [testLinkedAccounts, setTestLinkedAccounts] = useState<LinkedAccount[]>([]);
  const [selectedTestLinkedAccountId, setSelectedTestLinkedAccountId] = useState<
    string | undefined
  >(undefined);

  // Validation state
  const [isPageError, setIsPageError] = useState(false);
  const [isLoopError, setIsLoopError] = useState(false);

  const [isOverrideLastRunAtError, setIsOverrideLastRunAtError] = useState<boolean>(false);
  const [overrideLastRunAtErrorText, setOverrideLastRunAtErrorText] = useState<string | undefined>(
    undefined
  );

  // Fetch test linked accounts
  const { fetchTestLinkedAccounts } = useFetchTestLinkedAccounts({
    setIsLoadingTestLinkedAccounts,
    setTestLinkedAccounts,
    setSelectedTestLinkedAccount: setSelectedTestLinkedAccountId,
  });

  useEffect(() => {
    fetchTestLinkedAccounts(integrationID, blueprintCategory);
  }, [blueprintCategory, integrationID]);

  // Handle form submission
  const handleConfirm = () => {
    if (!selectedTestLinkedAccountId) return;

    onConfirm({
      linkedAccountId: selectedTestLinkedAccountId,
      overrideLastRunAt,
      maxPageIterations,
      maxLoopIterations,
      disableFilterByDate,
      respectLinkedAccountScopes,
    });
    onClose();
  };

  const isFormValid =
    !!selectedTestLinkedAccountId && !isOverrideLastRunAtError && !isPageError && !isLoopError;

  return (
    <Dialog
      title="Configure Common Model diff run"
      variant="md"
      open={open}
      onClose={onClose}
      onSecondaryButtonClick={onClose}
      onPrimaryButtonClick={handleConfirm}
      primaryButtonDisabled={!isFormValid}
      primaryButtonText="Run diff"
      primaryButtonVariant={ButtonVariant.PrimaryGray}
    >
      <div className="space-y-4">
        <LinkedAccountSelector
          isLoading={isLoadingTestLinkedAccounts}
          linkedAccounts={testLinkedAccounts}
          selectedAccountId={selectedTestLinkedAccountId}
          onSelectAccount={setSelectedTestLinkedAccountId}
        />
        <OverrideLastRunAtField
          value={overrideLastRunAt}
          onChange={setOverrideLastRunAt}
          error={isOverrideLastRunAtError}
          errorText={overrideLastRunAtErrorText}
          setError={setIsOverrideLastRunAtError}
          setErrorText={setOverrideLastRunAtErrorText}
        />
        <CappedNumberField
          label="Max page iterations"
          description="The maximum number of pages to request in any APIRequestLoop step before not fetching any
        more pages"
          placeholder="Enter max page iterations"
          value={maxPageIterations}
          max={50}
          capErrorMessage={
            !maxPageIterations
              ? "Page count must have a minimum value of 1"
              : "Page count is capped at 50 iterations per run to prevent processing extremely large syncs"
          }
          onChange={setMaxPageIterations}
          onErrorChange={setIsPageError}
        />
        <CappedNumberField
          label="Max loop iterations"
          description="The default maximum number of items to loop over in any loop before breaking early"
          placeholder="Enter max loop iterations"
          value={maxLoopIterations}
          max={100}
          capErrorMessage={
            !maxLoopIterations
              ? "Loop count must have a minimum value of 1"
              : "Loop count is capped at 100 iterations per run to prevent processing extremely large syncs"
          }
          onChange={setMaxLoopIterations}
          onErrorChange={setIsLoopError}
        />
        <CMDCheckboxLabel
          label="Disable filter by date"
          checked={disableFilterByDate}
          onChange={setDisableFilterByDate}
        />
        <CMDCheckboxLabel
          label="Respect Linked Account scopes"
          checked={respectLinkedAccountScopes}
          onChange={setRespectLinkedAccountScopes}
        />
      </div>
    </Dialog>
  );
};

export default CommonModelDiffRunModal;
